import { type CSSProperties } from "react";
import { Wallet, Plus } from "lucide-react";
import { BankLogo } from "../../BankTransactions/components/BankLogo";
import type { AccountSummary, BankBrand } from "../overviewTypes";

/**
 * Horizontal strip of connected accounts, hairline-divider columns.
 *
 * Responsive:
 *   desktop (>960px): auto-fit grid (4-wide for typical account counts)
 *   tablet (641-960px): 2 columns
 *   phone (<=640px): 1 column stack with hairline dividers between rows
 */

interface AccountsStripProps {
  accounts: AccountSummary[];
  onAccountClick: (account: AccountSummary) => void;
  onAddAccount: () => void;
}

const PALETTE = {
  ink: "#0E1A1A",
  ink3: "#3A4A52",
  ink4: "#6B7684",
  lineSoft: "#ECEFF3",
  teal: "#0F6E56",
  vela: "#A855F7",
};

const SHORT_NAMES: Record<BankBrand, string> = {
  rbc: "RBC",
  td: "TD",
  scotiabank: "SB",
  bmo: "BMO",
  cibc: "CIBC",
  tangerine: "TG",
  simplii: "SM",
  national_bank: "NBC",
  desjardins: "DJ",
  other: "$",
};

function formatBalance(value: number): string {
  return new Intl.NumberFormat("en-CA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function syncLabel(iso?: string): string {
  if (!iso) return "Manual";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const hrs = Math.round((now - then) / (1000 * 60 * 60));
  if (hrs < 1) return "Synced just now";
  if (hrs < 24) return `Synced ${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return `Synced ${days}d ago`;
}

export function AccountsStrip({ accounts, onAccountClick, onAddAccount }: AccountsStripProps) {
  if (accounts.length === 0) return null;

  return (
    <section style={sectionStyle}>
      <div style={headRowStyle}>
        <p style={sectionLabelStyle}>Your accounts</p>
        <button type="button" style={linkStyle} onClick={onAddAccount}>
          <Plus size={12} strokeWidth={2.5} /> Add account
        </button>
      </div>
      <div className="novala-accounts-grid" style={stripStyle}>
        {accounts.map((account, i) => (
          <button
            key={account.id}
            type="button"
            onClick={() => onAccountClick(account)}
            className="novala-accounts-col"
            style={{
              ...colStyle,
              paddingLeft: i === 0 ? 0 : 24,
              paddingRight: i === accounts.length - 1 ? 0 : 24,
              borderRight: i === accounts.length - 1 ? "none" : `1px solid ${PALETTE.lineSoft}`,
            }}
          >
            {account.bankBrand === "other" ? (
              <span style={cashIconStyle}><Wallet size={18} strokeWidth={1.8} /></span>
            ) : (
              <BankLogo
                code={account.bankBrand.toUpperCase()}
                shortName={SHORT_NAMES[account.bankBrand]}
                size="md"
              />
            )}
            <div style={textWrapStyle}>
              <p style={nameStyle}>
                {account.accountName}
                {account.accountNumberLast4 !== "----" && ` · ${account.accountNumberLast4}`}
              </p>
              <p style={balanceStyle}>
                {formatBalance(account.balance)}
                <span style={curStyle}>{account.currency}</span>
              </p>
              <p style={metaStyle}>
                {account.isConnected && <span style={vDotStyle}>V</span>}
                {syncLabel(account.lastSyncedAt)}
              </p>
            </div>
          </button>
        ))}
      </div>
      <style>{`
        @media (max-width: 640px) {
          .novala-accounts-grid {
            grid-template-columns: 1fr !important;
          }
          .novala-accounts-col {
            padding: 12px 0 !important;
            border-right: 0 !important;
            border-bottom: 1px solid ${PALETTE.lineSoft};
          }
          .novala-accounts-col:last-child { border-bottom: 0 !important; }
        }
        @media (min-width: 641px) and (max-width: 960px) {
          .novala-accounts-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .novala-accounts-col {
            padding: 12px 20px !important;
          }
          .novala-accounts-col:nth-child(odd) { border-right: 1px solid ${PALETTE.lineSoft} !important; padding-left: 0 !important; }
          .novala-accounts-col:nth-child(even) { border-right: 0 !important; padding-right: 0 !important; }
          .novala-accounts-col:nth-last-child(-n+2) { border-bottom: 0; }
          .novala-accounts-col:not(:nth-last-child(-n+2)) { border-bottom: 1px solid ${PALETTE.lineSoft}; }
        }
      `}</style>
    </section>
  );
}

const sectionStyle: CSSProperties = { marginBottom: 32 };

const headRowStyle: CSSProperties = {
  display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 14,
};

const sectionLabelStyle: CSSProperties = {
  fontSize: 11.5, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase",
  color: PALETTE.ink3, margin: 0,
};

const linkStyle: CSSProperties = {
  background: "transparent", border: 0, color: PALETTE.teal,
  fontSize: 12.5, fontWeight: 600, cursor: "pointer", padding: 0,
  fontFamily: "inherit", display: "inline-flex", alignItems: "center", gap: 4,
};

const stripStyle: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: 0,
};

const colStyle: CSSProperties = {
  display: "flex", alignItems: "center", gap: 12,
  padding: "0 24px",
  background: "transparent", border: 0, textAlign: "left",
  cursor: "pointer", fontFamily: "inherit",
  minHeight: 54,
};

const cashIconStyle: CSSProperties = {
  width: 36, height: 36, borderRadius: 7,
  background: "#fff", border: `1px solid ${PALETTE.lineSoft}`,
  display: "inline-flex", alignItems: "center", justifyContent: "center",
  color: PALETTE.ink3, flexShrink: 0,
};

const textWrapStyle: CSSProperties = {
  display: "flex", flexDirection: "column", gap: 1, minWidth: 0,
};

const nameStyle: CSSProperties = {
  fontSize: 12, color: PALETTE.ink3, margin: 0,
};

const balanceStyle: CSSProperties = {
  fontSize: 17, color: PALETTE.ink, fontWeight: 700,
  letterSpacing: "-0.01em", margin: 0,
  fontVariantNumeric: "tabular-nums",
};

const curStyle: CSSProperties = {
  fontSize: 11, fontWeight: 500, color: PALETTE.ink4, marginLeft: 2,
};

const metaStyle: CSSProperties = {
  fontSize: 10.5, color: PALETTE.ink4, margin: "2px 0 0",
};

const vDotStyle: CSSProperties = {
  display: "inline-flex", alignItems: "center", justifyContent: "center",
  width: 10, height: 10, borderRadius: "50%",
  background: PALETTE.vela, color: "#fff",
  fontSize: 7, fontWeight: 800, marginRight: 4, verticalAlign: "middle",
};